import * as React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Checkbox, FormControlLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import FilterListIcon from '@mui/icons-material/FilterList';
import Header from '../../components/Header';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditInfluencer from './EditInfluencer';

const Influencers = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    Segment: [],
    Category: [],
    Location: [],
    Gender: [],
    FollowersRange: [],
    Language: [],
  });
  const [openFilter, setOpenFilter] = useState(null); // Track which filter dropdown is open
  const [dropdownPosition, setDropdownPosition] = useState({}); // Track dropdown positions dynamically
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);

  const filterOptions = {
    Segment: ["Comedy", "Drama", "Lifestyle", "Fitness"],
    Category: ["Micro", "Macro", "Nano"],
    Location: ["Delhi", "Mumbai", "Bangalore", "Chennai"],
    Gender: ["Male", "Female"],
    FollowersRange: ["upcoming feature"], // Sample range
    Language: ["English", "Hindi", "Spanish"]
  };

  // Reference for each filter button to calculate dropdown positions
  const filterButtonsRef = useRef({});

  // Fetch influencers with filters
  useEffect(() => {
    const fetchInfluencers = async () => {
      const API_URL = 'https://api.dmaverix.com/api/v2/influencers/filter';
      const JWT_TOKEN = localStorage.getItem('token');

      if (!JWT_TOKEN) {
        setError('No token found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const requestBody = {
          ...filters,
          page: currentPage,
        };

        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${JWT_TOKEN}`,
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

        if (response.ok && data.success && Array.isArray(data.data)) {
          const rowsWithId = data.data.map((row, index) => ({
            ...row,
            id: row.uid || index + 1,
          }));
          setInfluencers(rowsWithId);
          setTotalPages(data.totalPages || 1);
        } else {
          throw new Error(data.message || 'Invalid API response format.');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInfluencers();
  }, [filters, currentPage]); // Refetch whenever filters or page changes

  const handleChangeFilter = (category, value) => {
    setFilters((prevFilters) => {
      const updatedCategory = prevFilters[category].includes(value)
        ? prevFilters[category].filter((item) => item !== value)
        : [...prevFilters[category], value];
      return { ...prevFilters, [category]: updatedCategory };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      Segment: [],
      Category: [],
      Location: [],
      Gender: [],
      FollowersRange: [],
      Language: [],
    });
  };
  const handleEditClick = (influencer) => {
    setSelectedInfluencer(influencer);
    setOpenEditModal(true);
    // console.log('Edit clicked for:', row);
    // Add your edit logic here, such as opening a modal with the row's details
  };
  const handleCloseModal = () => {
    setOpenEditModal(false);
    setSelectedInfluencer(null);
  }

  const handleDelete = async (influencer) => {
    const API_URL = `https://api.dmaverix.com/api/v2/influencers/${influencer.id}`;
    const JWT_TOKEN = localStorage.getItem('token');

    if (!JWT_TOKEN) {
      return;
    }

    try {
      await fetch(API_URL, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${JWT_TOKEN}`,
        },
      });
      setInfluencers(influencers.filter((item) => item.id !== influencer.id));
    } catch (error) {
      console.error('Error deleting influencer:', error);
    }
    // Add your delete logic here, such as showing a confirmation dialog
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, headerAlign: 'left' },
    { field: 'IGHandle', headerName: 'Instagram Handle', flex: 1, headerAlign: 'left' },
    { field: 'IGLink', headerName: 'Instagram Link', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'Contact', headerName: 'Contact', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'Followers', headerName: 'Followers', type: 'number', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'AvgViews', headerName: 'Avg Views', type: 'number', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'Segment', headerName: 'Segment', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'Category', headerName: 'Category', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'Location', headerName: 'Location', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'Gender', headerName: 'Gender', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'EngRate', headerName: 'EngRate', align: 'left', flex: 1, headerAlign: 'left' },
    { field: 'Language', headerName: 'Language', align: 'left', flex: 1, headerAlign: 'left' },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <EditIcon
            style={{ cursor: 'pointer', color: '#4caf50' }}
            onClick={() => handleEditClick(params.row)}
          />
          <DeleteIcon
            style={{ cursor: 'pointer', color: '#f44336' }}
            onClick={() => handleDelete(params.row)}
          />
        </div>
      ),
      sortable: false,
    },
  ];

  const hasSelectedFilters = Object.values(filters).some((filterList) => filterList.length > 0);

  const getDropdownPosition = (category) => {
    if (filterButtonsRef.current[category]) {
      const rect = filterButtonsRef.current[category].getBoundingClientRect();
      return {
        top: rect.bottom + window.scrollY, // Position dropdown below the button
        left: rect.left, // Align to the left of the button
      };
    }
    return { top: 0, left: 0 }; // Default position if reference is not available
  };

  return (
    <Box m="20px">
      <Box fontFamily="Inter" alignItems="center">
        <Header title="Influencers" subtitle="List of Influencers" />
        <div style={{ fontFamily: 'Arial, sans-serif' }}>
          {/* Filter Button */}
          <div className='main-filter' style={{ display: 'flex', marginBottom: '10px', justifyContent: 'space-between' }}>
            <div className='filter-buttons'>
              <Button sx={{
                marginRight: '10px',
                backgroundColor: colors.greenAccent[500],
                fontFamily: 'Arial',
                fontSize: '16px',
                fontWeight: '500',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f6eded'
                }
              }}>
                <FilterListIcon style={{ marginRight: '10px' }} />
                Filters
              </Button>

              {/* Individual filter buttons */}
              {Object.keys(filterOptions).map((category) => (
                <Button
                  key={category}
                  ref={(el) => filterButtonsRef.current[category] = el} // Reference to each filter button
                  onClick={() => {
                    setOpenFilter(openFilter === category ? null : category);
                    const position = getDropdownPosition(category);
                    setDropdownPosition((prevState) => ({
                      ...prevState,
                      [category]: position,
                    }));
                  }} // Toggle dropdown for selected filter
                  variant="contained"
                  color="primary"
                  sx={{
                    marginRight: '10px',
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "16px",
                    fontWeight: "bold",
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'
                    }
                  }}
                >
                  {category}
                  <ArrowDropDownIcon style={{ margin: '0px 0px 0px 10px', padding: '0px' }} />
                </Button>
              ))}
            </div>

            <div className='clear-all'>
              {/* Clear All Button */}
              <Button
                onClick={clearAllFilters}
                variant="contained"
                color="secondary"
                sx={{
                  backgroundColor: '#f6eded',
                  fontFamily: 'Arial',
                  fontSize: '16px',
                  fontWeight: '600',

                }}
              >
                Clear All
              </Button>
            </div>

          </div>

          {/* Render the dropdown for the selected filter */}
          {openFilter && (
            <div
              style={{
                position: 'absolute',
                top: dropdownPosition[openFilter]?.top || 0,
                left: dropdownPosition[openFilter]?.left || 0,
                border: '1px solid #ccc',
                borderRadius: '10px',
                padding: '10px',
                backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f7f7f7',
                zIndex: 999,
                display: 'grid',
                gap: '10px',
                color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                maxWidth: 'auto',
                minWidth: '150px',
                marginTop: '10px'
              }}
            >
              <h4 style={{ margin: '0px', padding: '0px' }}>{openFilter}</h4>
              {filterOptions[openFilter].map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={filters[openFilter].includes(option)}
                      onChange={() => handleChangeFilter(openFilter, option)}
                    />
                  }
                  label={option}
                  style={{
                    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                  }}
                />
              ))}
            </div>
          )}

          {/* Display selected filters if any are selected */}
          {hasSelectedFilters && (
            <div style={{ marginTop: '20px', border: '1px solid lightgray', color: theme.palette.mode === 'dark' ? '#fff' : '#000', padding: '5px' }}>
              <strong>Selected Filters:</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {Object.keys(filters).map((category) =>
                  filters[category].map((selectedItem) => (
                    <div
                      key={selectedItem}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f7f2f2',
                        borderRadius: '15px',
                      }}
                    >
                      {selectedItem}
                      <button
                        onClick={() => handleChangeFilter(category, selectedItem)}
                        style={{
                          color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                          backgroundColor: theme.palette.mode === 'dark' ? '#7c7878' : '#fb7979',
                          border: 'none',
                          borderRadius: '10px',
                          marginLeft: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </Box>

      {/* Error Handling */}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {/* DataGrid */}
      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={influencers}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          checkboxSelection
          disableSelectionOnClick
          components={{ Toolbar: GridToolbar }}
          pagination
          page={currentPage - 1}
          onPageChange={(newPage) => handlePageChange(newPage + 1)}
          paginationMode="server"
          rowCount={totalPages * 10}
          sx={{
            '& .MuiDataGrid-root': {
              backgroundColor: theme.palette.mode === 'dark' ? colors.background : '#fff',
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
            '& .MuiDataGrid-cell': {
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: colors.blueAccent[700],
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: colors.blueAccent[700],
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#444' : '#f1f1f1',
            },
            '& .MuiDataGrid-toolbarContainer': {
              backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f4f4f4',
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
            '& .MuiButtonBase-root': {
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
            '& .MuiSvgIcon-root': {
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
            '& .MuiDataGrid-filterForm': {
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
          }}
        />
      </div>

      {/* Modal for Edit Influencer */}
      <Dialog open={openEditModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <div style={{ display: 'flex', justifyContent: 'space-between', background:'#adadad' }}>
          <DialogTitle variant='h4'> Edit Influencer </DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseModal} >
              <CloseIcon/>
            </Button>
          </DialogActions>
        </div>
        <DialogContent>
          <EditInfluencer setInfluencers={setInfluencers} influencer={selectedInfluencer} onClose={handleCloseModal} />
        </DialogContent>

      </Dialog>
    </Box>
  );
};

export default Influencers;


